import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Callout } from '@/shared/components/feedback/Callout';
import { ChoiceCards } from '@/shared/components/selection/ChoiceCards/ChoiceCards';
import { type SelectionOption } from '@/shared/components/selection/types/selection-option.types';
import { cn } from '@/shared/utils/cn/cn.util';

import {
  MODE_ORDER,
  UNCHANGED,
  type SectionDefaultChoice,
  type SectionModeChoice,
} from '../../utils/question-visibility/question-visibility.util';
import { VisibilityColumnCaptions } from '../VisibilityColumnCaptions';
import { VisibilityContextFieldset } from '../VisibilityContextFieldset';

import {
  type SectionVisibilityDefaultsMatrixProps,
  type SectionVisibilityDefaultsRow,
} from './SectionVisibilityDefaultsMatrix.types';
import { useSectionVisibilityDefaultsMatrixLogic } from './useSectionVisibilityDefaultsMatrixLogic';

/**
 * What to apply to **every** question of one section — the same 2×2 as the
 * per-question matrix, over a value where a facet may simply not be named.
 *
 * The difference is the whole point of the endpoint behind it: it takes a
 * **patch**, so "leave this alone" has to be a choice an operator can make and
 * see, not the absence of an interaction. Hence a fourth card rather than a
 * checkbox gating a control — a gated control still shows a value, and a value
 * on screen that is not going to be sent is the misreading this surface exists
 * to prevent.
 */
export function SectionVisibilityDefaultsMatrix({
  value,
  onChange,
  isDisabled = false,
  describedById,
}: SectionVisibilityDefaultsMatrixProps) {
  const { t } = useTranslation('questions');
  const groupId = useId();
  const { rows, setMode, setStart } = useSectionVisibilityDefaultsMatrixLogic({ value, onChange });

  // Its own hint JOINS the field's description rather than sitting beside it
  // with an id nothing references — `VisibilityMatrix`'s own reasoning.
  const hintId = `${groupId}-hint`;
  const describedBy = [describedById, hintId].filter((id) => id !== undefined).join(' ');

  return (
    <div
      // The grip a measured walk needs: this dialog's form is not the only thing
      // on the page wearing a `<fieldset>` (P10.6e1's finding, one surface over).
      data-section-visibility-defaults=""
      className="flex flex-col gap-md"
      role="group"
      aria-label={t('sections.visibilityDefaults.fieldLabel')}
      aria-describedby={describedBy}
    >
      <p id={hintId} className="text-caption text-muted-foreground">
        {t('sections.visibilityDefaults.hint')}
      </p>

      <VisibilityColumnCaptions />

      {rows.map((row) => (
        <SectionVisibilityDefaultsFieldset
          key={row.context}
          row={row}
          isDisabled={isDisabled}
          onModeChange={(choice) => {
            setMode(row.context, choice);
          }}
          onStartChange={(choice) => {
            setStart(row.context, choice);
          }}
        />
      ))}
    </div>
  );
}

interface SectionVisibilityDefaultsFieldsetProps {
  row: SectionVisibilityDefaultsRow;
  isDisabled: boolean;
  onModeChange: (choice: SectionModeChoice) => void;
  onStartChange: (choice: SectionDefaultChoice) => void;
}

/** The caller's `t`. */
type Translate = (key: string, params?: Record<string, unknown>) => string;

/** Leave-it-alone first, then every mode — the order the patch itself reads in. */
function modeOptions(t: Translate): readonly SelectionOption[] {
  return [
    {
      value: UNCHANGED,
      label: t('sections.visibilityDefaults.unchanged'),
      description: t('sections.visibilityDefaults.unchangedModeHint'),
    },
    ...MODE_ORDER.map((mode) => ({
      value: mode,
      label: t(`visibility.mode.${mode}`),
      description: t(`visibility.modeHint.${mode}`),
    })),
  ];
}

/** The starting position is THREE states here, which is why it is not a switch. */
function startOptions(t: Translate): readonly SelectionOption[] {
  return [
    {
      value: UNCHANGED,
      label: t('sections.visibilityDefaults.unchanged'),
      description: t('sections.visibilityDefaults.unchangedStartHint'),
    },
    {
      value: 'visible',
      label: t('sections.visibilityDefaults.startVisible'),
      description: t('sections.visibilityDefaults.startVisibleHint'),
    },
    {
      value: 'hidden',
      label: t('sections.visibilityDefaults.startHidden'),
      description: t('sections.visibilityDefaults.startHiddenHint'),
    },
  ];
}

/**
 * One context's row.
 *
 * Module scope, not an inline closure: an identity created during render
 * remounts both `ChoiceCards` on every pass, which React Compiler's
 * `static-components` rule forbids at error level.
 */
function SectionVisibilityDefaultsFieldset({
  row,
  isDisabled,
  onModeChange,
  onStartChange,
}: SectionVisibilityDefaultsFieldsetProps) {
  const { t } = useTranslation('questions');
  const rowId = useId();
  const startDisabledId = `${rowId}-start-disabled`;
  const contextLabel = t(`visibility.context.${row.context}`);
  const startLabel = t('sections.visibilityDefaults.start');
  const modes = useMemo(() => modeOptions(t), [t]);
  const starts = useMemo(() => startOptions(t), [t]);

  return (
    <VisibilityContextFieldset
      legend={contextLabel}
      decision={
        <ChoiceCards
          label={contextLabel}
          items={modes}
          value={row.mode}
          isDisabled={isDisabled}
          className="min-w-0"
          onValueChange={(next) => {
            onModeChange(next as SectionModeChoice);
          }}
        />
      }
      start={
        <div className={cn('flex min-w-0 flex-col gap-xs', !row.isStartMeaningful && 'opacity-60')}>
          {/* Only where the shared column captions are NOT — they are `hidden
              md:grid`, so showing this at every width printed two names for one
              column, in two different words, one above the other (caught by the
              stills). `aria-hidden` because the group below already carries the
              same words in its own accessible name. */}
          <span aria-hidden="true" className="text-label font-medium text-foreground md:hidden">
            {startLabel}
          </span>

          {row.isStartMeaningful ? null : (
            <p id={startDisabledId} className="text-caption text-muted-foreground">
              {t('sections.visibilityDefaults.startDisabled')}
            </p>
          )}

          <ChoiceCards
            label={`${contextLabel} — ${startLabel}`}
            items={starts}
            value={row.start}
            // Meaningless once this context is forced: the member is never
            // asked, so a starting position would answer nobody. The patch has
            // already dropped the key — this only stops it being re-added.
            isDisabled={isDisabled || !row.isStartMeaningful}
            aria-describedby={row.isStartMeaningful ? undefined : startDisabledId}
            className="min-w-0"
            onValueChange={(next) => {
              onStartChange(next as SectionDefaultChoice);
            }}
          />
        </div>
      }
      footer={
        <>
          <p className="text-caption text-muted-foreground">
            {t(`sections.visibilityDefaults.outcome.${row.outcomeKey}`)}
          </p>

          {row.showsStricterWarning ? (
            <Callout liveness="alert">{t('visibility.stricterWarning')}</Callout>
          ) : null}
        </>
      }
    />
  );
}
